// =============================================================================
// ui/auth/recovery.ts — "a password reset is half-finished".
// -----------------------------------------------------------------------------
// 🚨 WHAT THIS IS, AND WHAT IT IS HONESTLY NOT.
//
// It is a UX gate. It makes the reset screen survive a refresh, a restored tab
// and a pasted URL, so the flow cannot be lost the way it was in beta (#31,
// reported by a real user who refreshed to let their password manager capture
// the new password and found themselves logged in instead).
//
// 🔴 IT IS NOT A SECURITY CONTROL, AND MUST NOT BE DESCRIBED AS ONE. A recovery
// session is a real session; anyone holding its token can call the API directly
// and never load this app at all. No client-side gate can change that. The
// security of this flow lives in three server-side places instead:
//
//   1. a GLOBAL SIGN-OUT when the password changes  — see ResetPassword.tsx.
//      This is the one that matters: OWASP requires a credential change to
//      invalidate sessions established with the old credential, and without it
//      "reset my password" does not evict anyone who is already in.
//   2. the recovery link's TTL                       — Supabase dashboard.
//   3. leaked-password protection                    — Supabase dashboard.
//
// ── WHY A STORED MARKER AND NOT THE TOKEN ──
//
// The first design read it off the session, so it could not be cleared by
// whoever held the tab. That was investigated and rejected on evidence:
// `AMREntry.method` in @supabase/auth-js 2.110.8 enumerates password, otp,
// oauth, totp, mfa/*, anonymous, sso/saml, magiclink, web3 — with **no
// `recovery`**, because a recovery link is an OTP type rather than a method.
// `user.recovery_sent_at` is worse: it records when an email was SENT and stays
// set afterwards, so it would false-positive for ever.
//
// ⚠ And even if it worked it would buy nothing, because of the paragraph above:
// a token-derived gate is no more enforcing than a stored one. So the simple
// mechanism is the right one, and the effort belongs in the server-side items.
//
// `PASSWORD_RECOVERY` is a first-class auth event and fires exactly once, on the
// load that consumes the link. That single moment is what gets recorded here.
// =============================================================================

/** Namespaced so it cannot collide with anything else on this origin. */
const KEY = 'tc_password_recovery_pending';

/**
 * ⚠ sessionStorage, NOT localStorage, and the difference is the point.
 *
 * It survives a RELOAD — the reported case — but not a tab close. A marker that
 * outlived the browsing context could strand someone on a reset screen for an
 * account they can sign into perfectly well, which is a worse failure than the
 * one being fixed. The recovery session it gates is per-tab too, so the two
 * lifetimes match.
 *
 * ⚠ Every call is wrapped. Safari in private mode throws on sessionStorage, and
 * an exception on the auth path would blank the app — far worse than the bug.
 */
export function markRecoveryPending(): void {
  try { sessionStorage.setItem(KEY, '1'); } catch { /* storage denied — see above */ }
}

export function clearRecoveryPending(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* storage denied — see above */ }
}

export function isRecoveryPending(): boolean {
  try { return sessionStorage.getItem(KEY) === '1'; } catch { return false; }
}

/**
 * Is this password acceptable?
 *
 * 🚨 DELIBERATELY FEW RULES — NIST SP 800-63B, and it is the opposite of what
 * most apps do. Length is the control that matters; forced composition
 * ("must contain a symbol") measurably pushes people toward `Password1!` and
 * buys nothing, and forced rotation makes it worse. So: a real length floor, no
 * character classes, and no expiry.
 *
 * ⚠ The check this does NOT do, because it belongs on the server: rejecting
 * known-breached passwords. Supabase does that against HaveIBeenPwned as a
 * project setting — it is a dashboard toggle, not code, and it is the single
 * highest-value addition to this function.
 */
export const MIN_PASSWORD_LENGTH = 12;

export function passwordProblem(pw: string, confirm: string): string | null {
  if (!pw || !confirm) return 'Both fields are required.';
  if (pw !== confirm) return 'The two passwords do not match.';
  if (pw.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters. Length is what makes a password strong — a long phrase beats a short one with symbols in it.`;
  }
  // The one composition-ish check worth having: a string of one repeated
  // character clears a length floor and is not a password.
  if (new Set(pw).size < 5) return 'That is too repetitive to be a password.';
  return null;
}
